import moment from 'moment'
import SvgCheckDoubleOutlined from '@/assets/svgs/status/check-double-outlined.jsx'
import SvgXCircleSolid from '@/assets/svgs/status/x-circle-solid.jsx'
import SvgArrowUpCircleOutlined from '@/assets/svgs/status/arrow-up-circle-outlined.jsx'
import SvgCheckHistoryOutlined from '@/assets/svgs/status/check-history-outlined.jsx'
import { withOrg } from '../constants'

/**
 * Presentation for each activity code, structured exactly like
 * SUBMITTAL_STATUS_TAGS in ../constants so the timeline and the status tag never
 * word the same workflow differently:
 *
 * - `whenSecondaryRequired` replaces the heading on an element that has a second
 *   approver, where the one-stage wording would mislead. "Submission Approved" on
 *   a precast submittal reads as finished when the submission has in fact only
 *   cleared the first of two reviews.
 * - `headingWithOrg` names the second approver. Used only when there is a name to
 *   use — an element can require a second approval without naming one, and
 *   "Approved by" with nothing after it is worse than the generic wording.
 */
const META = {
  SUBMITTALS_CREATED: {
    heading: 'Submission Created',
    icon: <SvgCheckHistoryOutlined className="DS__icon--warning" />,
    whenSecondaryRequired: { heading: 'Submitted to Consultant' },
  },
  SUBMITTALS_DRAFTED: {
    heading: 'Draft Saved',
    icon: <SvgCheckHistoryOutlined className="DS__icon--warning" />,
  },
  SUBMITTALS_UPDATED: {
    heading: 'Submission Updated',
    icon: <SvgArrowUpCircleOutlined className="DS__icon--info" />,
  },
  APPROVER_UPDATED: {
    heading: 'Approver Updated',
    icon: <SvgArrowUpCircleOutlined className="DS__icon--info" />,
  },
  SUBMITTALS_APPROVED: {
    heading: 'Submission Approved',
    icon: <SvgCheckDoubleOutlined className="DS__icon--success" />,
    whenSecondaryRequired: { heading: 'Approved by Consultant' },
  },
  SUBMITTALS_REJECTED: {
    heading: 'Submission Rejected',
    icon: <SvgXCircleSolid className="DS__icon--error" />,
    whenSecondaryRequired: { heading: 'Rejected by Consultant' },
  },
  SUBMITTALS_REPLACED: {
    heading: 'Submission Replaced',
    icon: <SvgXCircleSolid className="DS__icon--error" />,
    whenSecondaryRequired: { heading: 'Replaced' },
  },
  SUBMITTALS_DELETED: {
    heading: 'Submission Deleted',
    icon: <SvgXCircleSolid className="DS__icon--error" />,
  },
  SUBMITTALS_PASSED_DEADLINE: {
    heading: 'Submission Passed Deadline',
    icon: <SvgXCircleSolid className="DS__icon--error" />,
  },

  // Second-stage entries, which had no mapping at all until now — the consultant
  // saw the raw activity code for every one of them. Never keyed off `required`:
  // an element that does not require a second approval cannot produce one.
  SUBMITTALS_SUBMITTED_TO_SECONDARY: {
    heading: 'Submitted for Approval',
    headingWithOrg: 'Submitted to {org}',
    icon: <SvgArrowUpCircleOutlined className="DS__icon--info" />,
  },
  SUBMITTALS_SECONDARY_APPROVED: {
    heading: 'Submission Approved',
    headingWithOrg: 'Approved by {org}',
    icon: <SvgCheckDoubleOutlined className="DS__icon--success" />,
  },
  SUBMITTALS_SECONDARY_REJECTED: {
    heading: 'Submission Rejected',
    headingWithOrg: 'Rejected by {org}',
    icon: <SvgXCircleSolid className="DS__icon--error" />,
  },
}

/**
 * Turns the service's activity log rows into ActivityLogModal entries.
 *
 * `secondaryApproval` is the element's workflow — `{ required, label }`, the same
 * shape resolveStatusTag takes. Omitted, every heading reads in its single-stage
 * form, which is what an element with no second approver should show, and what
 * EMS keeps with the phase-2 flag off.
 *
 * An unrecognised code falls back to the raw activity string so a code the
 * frontend does not know about is visibly unstyled rather than blank.
 */
export const buildLogEntries = (logs = [], secondaryApproval) =>
  logs.map((log, idx) => {
    const { required, label } = secondaryApproval || {}
    const meta = META[log.activity] || { heading: log.activity, icon: null }
    const override =
      meta.headingWithOrg && label
        ? { heading: meta.headingWithOrg }
        : (required && meta.whenSecondaryRequired) || null
    const actor = log.consultant?.email || log.consultant?.name || log.user?.email
    const ts = log.created ? moment(log.created).format('DD/MM/YYYY, h:mm A') : ''
    // The fallback heading is the raw code, which a row can arrive without —
    // there is then nothing to substitute an approver into.
    const heading = override?.heading ?? meta.heading
    return {
      id: log.id ?? idx,
      icon: meta.icon,
      heading: heading ? withOrg(heading, label) : heading,
      details: log.message ? [{ label: 'Reason', value: log.message }] : undefined,
      meta: [actor && `by ${actor}`, ts && `on ${ts}`].filter(Boolean).join(' '),
    }
  })
